import styled from '@emotion/styled';
import {media, Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';
import Link from 'next/link';

import {Card} from 'UI/components/layout/Card';
import {PayloadImage} from 'UI/components/layout/PayloadImage';
import {H4, P} from 'UI/components/layout/Text';

import {FadeInAnimation} from 'UI/animations/shared';

import {useNewsCard} from './useNewsCard';
import {Calendar} from 'Images/icons';

import type {Media} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the properties for the `NewsCard` component.
 * This interface specifies all the required and optional properties needed to render a news article card with proper structure and content.
 * It ensures type safety for news data including publication details, content, and accessibility identifiers.
 */
interface ComponentProps {
    /**
     * The publication date of the news article in string format.
     * This date will be formatted and displayed alongside a calendar icon in the card.
     */
    date: string;
    /**
     * The main title or headline of the news article.
     * This text serves as the primary identifier and clickable element for the news content.
     */
    headline: string;
    /**
     * The unique identifier for the news article.
     * This ID is used for ARIA labeling to improve accessibility by connecting headline and description elements.
     */
    id: string;
    /**
     * The brief description or excerpt of the news article content.
     * This text provides a summary preview of the full article and is displayed with text truncation after 4 lines.
     */
    listDescription: string;
    /**
     * The optional image media object or string URL to display as the article thumbnail.
     * When provided, this image appears as a rounded thumbnail at the top of the news card with a fixed aspect ratio.
     */
    listImage?: Media | string | null;
    /**
     * The URL slug or path where the full news article can be accessed.
     * This path is used to create the clickable link that navigates users to the complete article content.
     */
    slug: string;
    /**
     * The test identifier for component testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'NewsCard'
     */
    testId?: string;
}

/**
 * The NewsCard component renders a clickable news article card with an image, date, headline, and description.
 * It displays news content in a structured card layout that includes visual elements like an image and formatted date with calendar icon.
 * The component is fully accessible with proper ARIA attributes and semantic HTML elements for screen readers and SEO.
 *
 * @param props                 The component props.
 * @param props.date            The publication date of the news article in string format.
 * @param props.headline        The main title/headline of the news article.
 * @param props.id              The unique identifier for the news article, used for ARIA labeling.
 * @param props.listDescription The brief description or excerpt of the news article content.
 * @param props.listImage       The optional image media object or string URL to display as the article thumbnail.
 * @param props.slug            The URL slug/path where the full news article can be accessed.
 * @param props.testId          The test identifier for component testing purposes.
 * @returns The NewsCard component JSX element wrapped in a Card layout.
 *
 * @example
 * ```tsx
 * <NewsCard
 *     date="2024-01-15"
 *     headline="Breaking News: New Technology Released"
 *     id="news-123"
 *     listDescription="A revolutionary new technology has been announced that will change the industry forever."
 *     listImage={mediaObject}
 *     slug="/news/breaking-news-new-technology"
 *     testId="news-card-123"
 * />
 * ```
 */
const NewsCard = observer(({
    date,
    headline,
    id,
    listDescription,
    listImage,
    slug,
    testId = 'NewsCard'
}: ComponentProps) => {
    const {formattedDate} = useNewsCard(date);

    return (
        <StylelessLink aria-describedby={`card-description-${id}`} aria-labelledby={`card-headline-${id}`} href={slug}>
            <Card data-cy={testId} span={8}>
                <ImageWrapper>
                    <Image image={listImage} />
                </ImageWrapper>
                <Spacer y={6} />
                <TimeDesc
                    as="time"
                    variants={FadeInAnimation}
                >
                    <Calendar color1="currentColor" />{formattedDate}
                </TimeDesc>
                <Spacer y={4} />
                <MotionH4 as="h3" id={`card-headline-${id}`} variants={FadeInAnimation}>{headline}</MotionH4>
                <Spacer y={4} />
                <StyledDesc id={`card-description-${id}`} variants={FadeInAnimation}>{listDescription}</StyledDesc>
            </Card>
        </StylelessLink>
    );
});

NewsCard.displayName = 'NewsCard';

export {NewsCard};

const ImageWrapper = styled.div`
    aspect-ratio: 200/141;
    width: 50%;
`;

const Image = styled(PayloadImage)`
    border-radius: 24px;
    height: 100%;
    width: 100%;
`;

const TimeDesc = styled(motion.create(P))`
    align-items: center;
    color: ${({theme}) => theme.colors.textSecondary};
    display: flex;
    font-size: 1.6rem;
    gap: ${spacing(3)};
    line-height: 1;

    ${media('md')} {
        font-size: 1.8rem;
    }

    ${media('lg')} {
        font-size: 2rem;
    }
`;

const MotionH4 = motion.create(H4);

const StyledDesc = styled(motion.create(P))`
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 4;
    display: -webkit-box;
    overflow: hidden;
    white-space: pre-wrap;
`;

const StylelessLink = styled(Link)`
    all: unset;
    cursor: pointer;
    display: contents;
`;